import type { WebContainer } from '@webcontainer/api';
import { workbenchStore } from '../../stores/workbench';
import { getHeadlessTemplateFiles } from '../../templates/headlessTemplate';
import { HOT_EDIT_BRIDGE_REL_PATHS } from '../../utils/mergedFiles';

function normalizePath(filePath: string): string {
  return filePath.replace(/\\/g, '/').replace(/^\/+/, '');
}

function buildPreserveSet(keep: string[]): Set<string> {
  const preserve = new Set<string>();

  for (const [path] of Object.entries(getHeadlessTemplateFiles())) {
    const normalized = normalizePath(path);
    if (normalized.startsWith('src/')) {
      preserve.add(normalized);
    }
  }

  for (const path of HOT_EDIT_BRIDGE_REL_PATHS) {
    preserve.add(normalizePath(path));
  }

  for (const path of keep) {
    const normalized = normalizePath(path);
    if (normalized.startsWith('src/')) {
      preserve.add(normalized);
    }
  }

  for (const [path, file] of Object.entries(workbenchStore.files.get())) {
    if (file?.modified) {
      const normalized = normalizePath(path);
      if (normalized.startsWith('src/')) {
        preserve.add(normalized);
      }
    }
  }

  return preserve;
}

async function listFilesRecursive(wc: WebContainer, dir: string): Promise<string[]> {
  const entries = await wc.fs.readdir(dir, { withFileTypes: true });
  const files: string[] = [];

  for (const entry of entries) {
    const fullPath = dir === '.' ? entry.name : `${dir}/${entry.name}`;
    if (entry.isDirectory()) {
      files.push(...(await listFilesRecursive(wc, fullPath)));
    } else if (entry.isFile()) {
      files.push(fullPath);
    }
  }

  return files;
}

/**
 * Remove generated files under src/ that are not in the preserve set.
 * Template infrastructure, seeded paths, and user-modified files are kept.
 */
export async function executePrune(wc: WebContainer, keep: string[]): Promise<string> {
  const preserve = buildPreserveSet(keep);
  let srcFiles: string[];

  try {
    srcFiles = await listFilesRecursive(wc, 'src');
  } catch {
    return 'Prune skipped: src/ not found';
  }

  const toRemove = srcFiles
    .map(normalizePath)
    .filter((path) => !preserve.has(path));

  for (const path of toRemove) {
    try {
      await wc.fs.rm(path);
      workbenchStore.removeFile(path);
    } catch {
      // Best-effort — stale paths should not block generation.
    }
  }

  return toRemove.length > 0
    ? `Pruned ${toRemove.length} stale file(s) under src/`
    : 'No stale files to prune under src/';
}
